import { useState } from 'react';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { Sheet } from '@/components/feedback/Sheet';
import { Notice } from '@/components/feedback';
import { useToast } from '@/components/feedback/Toast';
import { resolveContext } from '@/state/session';
import { formatDateTime } from '@/services/formatters';
import { recordAudit } from '@/services/audit';
import { requestStepUp, verifySca } from '@/mocks/api';
import { isMockApiError } from '@/mocks/errors';
import { db } from '@/mocks/db';
import { nowIso } from '@/services/clock';

/**
 * Security settings.
 *
 * TRACE:TT-R143-01 — mobile security settings expose Passkeys & MFA.
 * TRACE:TT-R143-02 — every security mechanism sits under this ONE Security
 * section; passkeys and MFA are not split across two places.
 * TRACE:TT-R144-01 — a Trusted devices screen is present here.
 * TRACE:TT-R144-02 — Trusted devices contains devices and sessions only. There
 * is deliberately no white-label/branding tab: tenant branding is a bank
 * operations concern and has no place in a customer security screen.
 * TRACE:XS-SMEM-WR-017 — devices, sessions, last activity and revoke controls,
 * with step-up confirmation before a revoke takes effect.
 */

type TrustedDevice = {
  readonly id: string;
  readonly name: string;
  readonly platform: string;
  readonly lastActiveIso: string;
  readonly current: boolean;
  readonly location: string;
};

const DEVICES: readonly TrustedDevice[] = [
  {
    id: 'dev_current',
    name: 'This device',
    platform: 'Mobile browser',
    lastActiveIso: '2026-07-27T09:00:00.000Z',
    current: true,
    location: 'Manchester, UK',
  },
  {
    id: 'dev_laptop',
    name: 'MacBook Pro',
    platform: 'Chrome on macOS',
    lastActiveIso: '2026-07-26T17:24:00.000Z',
    current: false,
    location: 'Manchester, UK',
  },
  {
    id: 'dev_old_phone',
    name: 'Pixel 7',
    platform: 'Chrome on Android',
    lastActiveIso: '2026-06-30T08:11:00.000Z',
    current: false,
    location: 'Leeds, UK',
  },
];

export function SecurityPage() {
  const resolved = resolveContext();
  const toast = useToast();
  const [revoked, setRevoked] = useState<readonly string[]>([]);
  const [pendingRevoke, setPendingRevoke] = useState<TrustedDevice | null>(null);
  // Step-up state: revoking a device is a security action, so it is gated by a
  // fresh challenge rather than a plain confirmation.
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  if (!resolved) return null;

  const beginRevoke = (device: TrustedDevice) => {
    setPendingRevoke(device);
    setCode('');
    setCodeError(null);
    setChallengeId(null);
    void requestStepUp(resolved.user.id, `device-revoke:${device.id}`).then((r) =>
      setChallengeId(r.data.challengeId),
    );
  };

  const confirmRevoke = async () => {
    if (!challengeId || !pendingRevoke) return;
    setCodeError(null);
    try {
      await verifySca(challengeId, code);
    } catch (e: unknown) {
      setCodeError(isMockApiError(e) ? e.message : 'That code was not correct.');
      setCode('');
      return;
    }
    revoke(pendingRevoke);
  };

  const revoke = (device: TrustedDevice) => {
    setRevoked((list) => [...list, device.id]);
    recordAudit({
      companyId: resolved.company.id,
      programmeId: db().programme.id,
      actorName: resolved.user.name,
      actorType: 'customer',
      action: 'device.revoked',
      objectType: 'device',
      objectId: device.id,
      customerVisible: true,
      summary: `Signed out ${device.name}`,
      before: { trusted: true },
      after: { trusted: false, revokedAt: nowIso() },
    });
    setPendingRevoke(null);
    toast.show({ message: `${device.name} was signed out.` });
  };

  const activeDevices = DEVICES.filter((d) => !revoked.includes(d.id));

  return (
    <>
      <MobileHeader title="Security" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <Notice tone="info" title="Everything security-related is here">
          Passkeys, two-factor authentication and your trusted devices are all managed on this one
          screen.
        </Notice>

        {/* Passkeys & MFA — one consolidated section (TT-R143-02). */}
        <section aria-labelledby="sec-auth">
          <SectionHeader title="Passkeys & MFA" id="sec-auth" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRow
                  title="Passkey"
                  meta="Sign in with your device biometrics"
                  end={<StatusBadge status="active" label="On" tone="success" />}
                  onClick={() =>
                    toast.show({
                      message: 'Passkey management opens your device settings in the full product.',
                    })
                  }
                  ariaLabel="Passkey, currently on. Manage passkey."
                />
              </li>
              <li>
                <ListRow
                  title="Authenticator app"
                  meta="Six-digit codes for sign-in and payments"
                  end={<StatusBadge status="active" label="On" tone="success" />}
                  onClick={() =>
                    toast.show({ message: 'Re-pairing an authenticator requires a security check.' })
                  }
                  ariaLabel="Authenticator app, currently on. Manage authenticator."
                />
              </li>
              <li>
                <ListRow
                  title="SMS backup code"
                  meta="Used only if your authenticator is unavailable"
                  end={<StatusBadge status="not-requested" label="Off" tone="neutral" />}
                  onClick={() =>
                    toast.show({ message: 'Adding an SMS backup requires a security check.' })
                  }
                  ariaLabel="SMS backup code, currently off. Turn on SMS backup."
                />
              </li>
            </ul>
          </div>
        </section>

        {/* Trusted devices — devices and sessions only (TT-R144-01/02). */}
        <section aria-labelledby="sec-devices">
          <SectionHeader title="Trusted devices" id="sec-devices" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {activeDevices.map((device) => (
                <li key={device.id}>
                  <ListRow
                    title={device.name}
                    meta={`${device.platform} · ${device.location} · Last active ${formatDateTime(device.lastActiveIso)}`}
                    end={
                      device.current ? (
                        <StatusBadge status="active" label="This device" tone="info" />
                      ) : (
                        <span className="tm-btn tm-btn--quiet tm-btn--sm" aria-hidden="true">
                          Sign out
                        </span>
                      )
                    }
                    {...(device.current
                      ? {}
                      : {
                          onClick: () => beginRevoke(device),
                          ariaLabel: `Sign out ${device.name}, ${device.platform}, last active ${formatDateTime(device.lastActiveIso)}`,
                        })}
                  />
                </li>
              ))}
            </ul>
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 8 }}>
            Signing out a device ends its session immediately. It will need full sign-in and a
            security code to return.
          </p>
        </section>

        <section aria-labelledby="sec-sessions">
          <SectionHeader title="Sign-in activity" id="sec-sessions" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRow
                  title="Successful sign-in"
                  meta={`${formatDateTime('2026-07-27T09:00:00.000Z')} · Manchester, UK`}
                />
              </li>
              <li>
                <ListRow
                  title="Successful sign-in"
                  meta={`${formatDateTime('2026-07-26T17:24:00.000Z')} · Manchester, UK`}
                />
              </li>
              <li>
                <ListRow
                  title="Failed sign-in attempt"
                  meta={`${formatDateTime('2026-07-24T02:13:00.000Z')} · Unknown location`}
                  end={<StatusBadge status="failed" label="Blocked" tone="danger" />}
                />
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Step-up before the revoke takes effect (XS-SMEM-WR-017). */}
      <Sheet
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        title={`Sign out ${pendingRevoke?.name ?? 'this device'}?`}
      >
        <p className="tm-muted">
          That device will be signed out straight away and will need to authenticate again. Confirm
          your identity to continue. This is recorded in your activity.
        </p>
        <div className="tm-field" style={{ marginBlockStart: 12 }}>
          <label className="tm-label" htmlFor="revoke-code">
            Authentication code
          </label>
          <input
            id="revoke-code"
            className="tm-input"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            aria-invalid={codeError ? true : undefined}
            aria-describedby={codeError ? 'revoke-code-error' : 'revoke-code-hint'}
            style={{ letterSpacing: '0.4em' }}
          />
          <div className="tm-field__error-slot">
            {codeError ? (
              <span className="tm-field__error" id="revoke-code-error">
                {codeError}
              </span>
            ) : (
              <span className="tm-hint" id="revoke-code-hint">
                Demo code: 481920
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="tm-btn tm-btn--danger tm-btn--block"
          disabled={code.length !== 6 || challengeId === null}
          onClick={() => void confirmRevoke()}
        >
          {challengeId === null ? 'Preparing…' : 'Sign out device'}
        </button>
      </Sheet>
    </>
  );
}
